import React, { useEffect, useMemo, useState } from 'react';

const FALLBACK_PASS_OPTIONS = [
  { slug: '1day', name: '1-Day Pass' },
  { slug: '2day', name: '2-Day Pass' },
  { slug: '3day', name: '3-Day Pass' },
];

const fieldLabelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: '0.8rem',
  color: '#374151',
  fontWeight: 600,
};

const inputStyle = {
  padding: '8px 10px',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: '0.85rem',
};

const AdminUnsoldAssignModal = ({
  open,
  onClose,
  onSubmit,
  loading,
  error,
  assignmentType,
  seat,
  seatCount = 1,
  passOptions = [],
  initialPassSlug = '',
  initialQuantity = 1,
  defaultEmail = '',
  defaultFirstName = '',
  defaultLastName = '',
  sendEmailOnAssign = false,
}) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [passSlug, setPassSlug] = useState('');

  const availablePassOptions = useMemo(() => {
    if (Array.isArray(passOptions) && passOptions.length > 0) return passOptions;
    return FALLBACK_PASS_OPTIONS;
  }, [passOptions]);

  useEffect(() => {
    if (!open) return;
    setEmail(defaultEmail || '');
    setFirstName(defaultFirstName || '');
    setLastName(defaultLastName || '');
    setQuantity(Math.max(1, parseInt(initialQuantity, 10) || 1));
    setPassSlug(initialPassSlug || availablePassOptions[0]?.slug || FALLBACK_PASS_OPTIONS[0].slug);
  }, [open, availablePassOptions, initialPassSlug, initialQuantity, defaultEmail, defaultFirstName, defaultLastName]);

  if (!open) return null;

  const canSubmit = email.trim() && firstName.trim();
  const normalizedSeatCount = Math.max(1, parseInt(seatCount, 10) || 1);
  const isMultiSeatAmpAssign = assignmentType === 'amp_seat' && normalizedSeatCount > 1;

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (!window.confirm('Please confirm the assignment details.')) return;
    if (!window.confirm('Final confirmation: proceed with this assignment?')) return;

    const payload = {
      email: email.trim(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      // Governed by the "Email ticket confirmation" toggle on the assignment page
      // (propagated in via prop), not a redundant checkbox in this modal.
      send_email: !!sendEmailOnAssign,
    };

    if (assignmentType === 'amp_seat') {
      payload.assignment_type = 'amp_seat';
      payload.seat_section = seat?.seat_section || '';
      payload.seat_row = seat?.seat_row || '';
      payload.seat_number = seat?.seat_number || '';
      payload.event_date = seat?.event_date || '';
    } else {
      payload.assignment_type = 'festival_pass';
      payload.pass_slug = passSlug || availablePassOptions[0]?.slug || FALLBACK_PASS_OPTIONS[0].slug;
      payload.quantity = Math.max(1, parseInt(quantity, 10) || 1);
    }

    onSubmit?.(payload);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 560, background: '#fff', borderRadius: 14, padding: '1rem 1.1rem', boxShadow: '0 18px 48px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#111827' }}>{assignmentType === 'amp_seat' ? (isMultiSeatAmpAssign ? 'Assign seats' : 'Assign a seat') : 'Assign Festival Pass'}</h3>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.8rem' }}>Admin-only assignment. Email and first name are required; last name is optional.</p>
          </div>
          <button onClick={onClose} disabled={loading} style={{ border: 'none', background: 'transparent', color: '#6b7280', fontSize: '1.4rem', cursor: loading ? 'not-allowed' : 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {assignmentType === 'amp_seat' ? (
          <div style={{ marginBottom: 12, padding: '8px 10px', borderRadius: 8, border: '1px solid #dbeafe', background: '#eff6ff', color: '#1e3a8a', fontSize: '0.8rem', fontWeight: 600 }}>
            {isMultiSeatAmpAssign
              ? `${normalizedSeatCount} seats selected${seat?.event_date ? ` · ${seat.event_date}` : ''}${seat?.section_label || seat?.seat_section ? ` · Starting from ${seat.section_label || seat.seat_section}` : ''}`
              : `${seat?.section_label || seat?.seat_section} · Row ${seat?.seat_row} · Seat ${seat?.seat_number} · ${seat?.event_date}`}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <label style={fieldLabelStyle}>
              Pass Type
              <select value={passSlug} onChange={(e) => setPassSlug(e.target.value)} disabled={loading} style={inputStyle}>
                {availablePassOptions.map((opt) => (
                  <option key={opt.slug} value={opt.slug}>{opt.name}</option>
                ))}
              </select>
            </label>
            <label style={fieldLabelStyle}>
              Quantity
              <input type="number" min="1" max="200" value={quantity} onChange={(e) => setQuantity(e.target.value)} disabled={loading} style={inputStyle} />
            </label>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label style={fieldLabelStyle}>
            First Name
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={loading} style={inputStyle} />
          </label>
          <label style={fieldLabelStyle}>
            Last Name (optional)
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={loading} style={inputStyle} />
          </label>
        </div>

        <label style={{ ...fieldLabelStyle, marginTop: 10 }}>
          Email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} style={inputStyle} />
        </label>

        {error ? <div style={{ marginTop: 10, padding: '0.75rem 0.9rem', borderRadius: 8, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', fontSize: '0.82rem', fontWeight: 600 }}>{error}</div> : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button onClick={onClose} disabled={loading} style={{ padding: '0.5rem 0.9rem', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: loading ? 'not-allowed' : 'pointer' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={!canSubmit || loading} style={{ padding: '0.5rem 1rem', borderRadius: 8, border: 'none', background: !canSubmit || loading ? '#93c5fd' : '#2563eb', color: '#fff', fontWeight: 700, cursor: !canSubmit || loading ? 'not-allowed' : 'pointer' }}>
            {loading ? 'Assigning…' : (assignmentType === 'amp_seat' ? (isMultiSeatAmpAssign ? 'Assign seats' : 'Assign a seat') : 'Assign Festival Pass')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminUnsoldAssignModal;
